import { CodingConstants, UIConstants } from '@constants';
import { RootState } from '@store';
import { widgets, WidgetState } from '@store/reducers/widget.reducer';
import { Identifier, MainMenuOptions, NavigationOptions, Redirection, UserInfo, WidgetInitDetails, Widgets, WidgetTemplate } from '@types';
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { config } from '../../config';
import { get, groupBy, has } from 'lodash';
import { setAdditionalProperties } from 'fmc-digital-common';

export const widgetsSlice = (state: RootState) => state.widgets || widgets;

export const nbpSlice = (state: RootState) => state.nbpCard;

export const selectCashOutResponse = createSelector([widgetsSlice], (slice: WidgetState): boolean => slice.metadata);

export const selectWidgets = createSelector([widgetsSlice], (slice: WidgetState): Widgets => slice.widgets);

export const selectNBP = createSelector([widgetsSlice], (data: WidgetState): any => data.nbpCard);

export const selectPropertyCheckValueError = createSelector([widgetsSlice], (data: WidgetState): any => data.propertyValueError);

export const selectPropertyCheckValue = createSelector([widgetsSlice], (data: WidgetState): any => data.propertyValue);

export const selectWidgetsData = createSelector([selectWidgets], (data: Widgets): WidgetTemplate[] => data.cards);

export const selectLoanStatusByLoanNumber = (loanNumber: string) => {
    return createSelector([selectWidgetsData], cardsList => {
        const data = cardsList && cardsList?.find(ele => ele.id === CodingConstants.loanInProgressWidgetId);
        if (!isEmpty(data)) {
            return data.initParam.find((ele: any) => ele.value === loanNumber)?.loanStatus;
        }
    });
};

export const selectRedirection = createSelector([selectWidgets], (data: Widgets): Redirection => data.redirect);

export const selectGetSelectedLoanNumber = createSelector([widgetsSlice], (slice: WidgetState): Identifier => slice.identifier);

export const selectNavigationOptions = createSelector([selectWidgets], (data: Widgets): NavigationOptions[] => {
    return data.navigationOptions || UIConstants.navigationOptions;
});
export const selectMainMenuOptions = createSelector([selectWidgets], (data: Widgets): MainMenuOptions[] => {
    return data?.mainMenuOptions;
});
export const selectLoan = createSelector([selectWidgets], (data: Widgets): Identifier[] => {
    return data?.profile?.identifiers;
});
export const selectUserInfo = createSelector([selectWidgets], (data: Widgets): UserInfo => {
    return (
        data?.profile?.userInfo ?? {
            firstName: '',
            lastName: '',
            userUUID: '',
            ssn: '',
            email: '',
            mobile: '',
            tcpaConsentRequired: false,
            portCode: ''
        }
    );
});

export const selectLogOut = createSelector([selectNavigationOptions], (navigationOptions): NavigationOptions => {
    if (!isEmpty(navigationOptions)) {
        return navigationOptions.find(ele => ele.id === 'logout_url');
    }
});

export const selectSortedWidgetsData = createSelector([selectWidgetsData], widgetsData => {
    return [...widgetsData].sort((a, b) => +a.rank - +b.rank);
});

export const selectOddRankWidgets = createSelector([selectWidgetsData], widgetData => {
    return widgetData.filter(card => +card.rank % 2 === 1).sort((a, b) => +a.rank - +b.rank);
});

export const selectEvenRankWidgets = createSelector([selectWidgetsData], widgetData => {
    return widgetData.filter(card => +card.rank % 2 === 0).sort((a, b) => +a.rank - +b.rank);
});

export const selectUserProfile = createSelector([selectNavigationOptions], (navigationOptions): NavigationOptions => {
    const { id, name, url } = UIConstants.userProfile;
    return navigationOptions.find(ele => ele.id === 'user_profile_url') || { id, name, url: `${config.env(url)}` };
});

export const selectMobileWidget = createSelector([selectSortedWidgetsData], widgetsList => {
    if (!isEmpty(widgetsList)) {
        return widgetsList.find(widget => widget.id === CodingConstants.mobileWidget);
    }
});

export const selectOnboardingWidgets = createSelector([selectSortedWidgetsData], widgetsList => {
    if (!isEmpty(widgetsList)) {
        return widgetsList.filter(widget => widget.group === CodingConstants.onboardingWidget).sort((a, b) => +a.rank - +b.rank);
    }
});

export const selectIsLoanInProgress = createSelector([selectLoan], loansList => {
    if (!isEmpty(loansList)) {
        return loansList?.filter(item => item?.type == CodingConstants.inProgressLoanType);
    }
});

export const selectCashOutOffer = createSelector([selectSortedWidgetsData], widgets => {
    if (!isEmpty(widgets)) {
        return widgets.find(widget => widget.id === CodingConstants.cashOutOfferId);
    }
});

export const inProgressCustomer = createSelector([selectLoan], loansList => {
    if (!isEmpty(loansList)) {
        return loansList?.find(item => item?.type == CodingConstants.inProgressLoanType);
    }
});

export const selectHasPaperless = createSelector([selectWidgetsData], widgetList => {
    if (!isEmpty(widgetList)) {
        return widgetList?.find(ele => ele.id === CodingConstants.paperLess);
    }
});

export const selectEligibleForEscrowScheduled = createSelector([selectWidgetsData], cardsList => {
    if (!isEmpty(cardsList)) {
        return cardsList?.find(ele => ele.id === CodingConstants.eligibleForEscrowScheduled);
    }
});

export const selectedAccountLoanType = createSelector(
    [selectGetSelectedLoanNumber],
    (slice: Identifier): string => slice?.loanType || 'default'
);

export const selectEligibleForEscrowComplete = createSelector([selectWidgetsData], cardsList => {
    if (!isEmpty(cardsList)) {
        return cardsList?.find(ele => ele.id === CodingConstants.eligibleForEscrowComplete);
    }
});

export const selectHasRefreshed = createSelector([selectWidgets], newWidgetList => {
    if (!isEmpty(newWidgetList)) {
        return newWidgetList.hasRefreshed;
    }
});

export const selectNaturalDisasterWidget = createSelector([selectWidgetsData], cardsList => {
    if (!isEmpty(cardsList)) {
        return cardsList?.find(ele => ele.id === CodingConstants.naturalDisaster);
    }
});

export const selectEligibleForNDByLoanNumber = (loanNumber: string) => {
    return createSelector([selectNaturalDisasterWidget], widget => {
        if (widget) {
            return widget.initParam.find(ele => ele.loanNumber === loanNumber);
        }
    });
};

export const selectBundleOffers = createSelector([selectWidgetsData], cards => {
    if (!isEmpty(cards)) {
        const offersGroup = groupBy(cards, 'group');
        if (has(offersGroup, 'Bundle_offers')) {
            return offersGroup.Bundle_offers;
        }
    }
});

export const selectOffers = createSelector([selectBundleOffers], offers => {
    if (!isEmpty(offers)) {
        return offers
            .map(eachOffer => {
                return eachOffer.initParam.map(ele => {
                    return {
                        loanNumber: ele.loanNumber,
                        offers: ele.offers.map((_each: any) => ({
                            offerType: _each?.product,
                            template: _each?.template,
                            offerDetails: Object.assign({}, _each?.offerDetails, { portCode: ele.portCode }),
                            isTeaser: _each?.teaserOfferFlg === 'Yes',
                            priorityNo: _each?.priorityNo
                        }))
                    };
                });
            })
            .flat();
    }
});

export const selectOfferLoanNumber = (_: any, loanNumber: string) => loanNumber;
export const selectOfferByLoan = createSelector([selectOffers, selectOfferLoanNumber], (offer, loanNumber) => {
    if (isEmpty(offer) || isEmpty(loanNumber)) return undefined;
    return offer.find((ele: any) => ele.loanNumber === loanNumber).offers;
});

export const selectTeaserOffer = createSelector(selectOfferByLoan, offers => {
    return isEmpty(offers) ? undefined : offers?.find(({ isTeaser }: { isTeaser: boolean }) => isTeaser);
});

export const selectOfferTypeSelected = createSelector(widgetsSlice, (slice: WidgetState) => slice.offerTypeSelected || '');

export const selectHasServicingLoan = createSelector([selectLoan], loansList => {
    if (!isEmpty(loansList)) {
        return loansList?.some(item => item?.type == CodingConstants.servicingLoanType);
    }
    return false;
});

/*
* {
                                id: 'first',
                                value: '1234567',
                                label: '14 Deer Creek Ave (...74)'
                            }
*
* */
export const createAccountOptions = createSelector(selectLoan, (identifiers: Identifier[]) => {
    if (isEmpty(identifiers))
        return [
            {
                label: 'Please Select',
                value: '0000000000',
                id: 'NA'
            }
        ];

    return identifiers.map(({ identifier, ...rest }: Identifier) => ({
        label: `${get(rest, 'propertyAddress.address1', '')} (...${identifier.slice(-2)})`,
        value: identifier,
        id: identifier
    }));
});

export const selectOnboardingCards = (onboardingFeatureFlags: any, id: string) =>
    createSelector([selectOnboardingWidgets], onboardingCards => {
        if (!isEmpty(onboardingCards)) {
            const isthereanyCards = onboardingCards.filter((e: WidgetTemplate) => e.id !== id);

            return onboardingFeatureFlags['onboarding'] && isthereanyCards
                ? onboardingCards.filter(x => onboardingFeatureFlags[x.featureFlagKey])
                : [];
        }
    });

export const selectByBundleOffers = createSelector(selectEvenRankWidgets, (widgets: WidgetTemplate[]) => {
    if (isEmpty(widgets)) return undefined;

    return widgets
        .filter((widget: WidgetTemplate) => widget.group === 'Bundle_offers')
        .reduce((result, current) => {
            const offers = get(current, ['initParam', '0', 'offers'], undefined);
            if (!offers) return result;
            return result.concat(offers);
        }, []);
});

export const selectBundleOfferTemplates = createSelector(selectByBundleOffers, (widgets: WidgetInitDetails[]) => {
    if (isEmpty(widgets)) return undefined;

    return widgets.map((widget: WidgetInitDetails) => {
        return widget.template;
    });
});

export const selectEscrowExists = createSelector(selectEvenRankWidgets, (widgets: WidgetTemplate[]) => {
    if (isEmpty(widgets)) return undefined;
    return widgets.find((widget: WidgetTemplate) => widget.id === 'escrow-analysis-complete');
});

export const selectServicingIdentifiers = createSelector([selectLoan], loansList => {
    if (!isEmpty(loansList)) {
        return loansList.filter(item => item?.type == CodingConstants.servicingLoanType);
    }
    return [];
});

export const selectBundleOfferTemplatesV2 = createSelector(selectByBundleOffers, (widgets: WidgetInitDetails[]) => {
    if (isEmpty(widgets)) return undefined;

    return widgets.map((widget: WidgetInitDetails) => {
        return { template: widget.template, offerDetails: widget.offerDetails };
    });
});
export const selectCurrentLoanAddress = createSelector([widgetsSlice, selectLoan], (slice: WidgetState) => {
    if (isEmpty(slice) || isEmpty(slice.widgets) || isEmpty(slice.identifier)) return 'your address';
    return (
        slice.identifier?.propertyAddress?.address1 ?? slice.widgets.profile.identifiers.at(0).propertyAddress?.address1 ?? 'your address'
    );
});
export const selectClearedNDLoans = createSelector(widgetsSlice, (slice: WidgetState) => {
    return slice.clearedLoansFromND;
});
// selectedLoan ? selectedLoan.identifier : loanList[0]?.identifier
export const selectSelectedLoanNumber = createSelector(
    [widgetsSlice, selectLoan],
    (slice: WidgetState, loanList: Array<Identifier>): Identifier => get(slice, ['identifier'], get(loanList, ['0'], undefined))
);
export const selectEligibleForNDByLoanNumberV2 = createSelector(
    [selectNaturalDisasterWidget, selectGetSelectedLoanNumber, selectClearedNDLoans],
    (widget, identifier: Identifier, clearedLoans: string[]) => {
        if (isEmpty(widget) || isEmpty(identifier.identifier) || clearedLoans.includes(identifier.identifier)) return false;
        return !isEmpty(
            widget.initParam.find(ele => {
                return ele.loanNumber === identifier.identifier;
            })
        );
    }
);

export const selectNotificationRequest = createSelector([selectServicingIdentifiers], (servicing: Identifier[]) => {
    if (isEmpty(servicing)) return undefined;

    return {
        accountLoanInfo: [...servicing.map(account => ({ accountNumber: account.identifier, loanType: get(account, ['loanType'], '') }))]
    };
});

export const selectLoanNumberSelected = createSelector([selectSelectedLoanNumber], (loan: Identifier) => {
    setAdditionalProperties('amplify', { loanNumber: loan?.identifier });
    return isEmpty(loan) ? '' : loan?.identifier;
});
export const selectLoanType = (_: any, accountLoanType: string) => accountLoanType;

export const selectFilteredMenuOptions = createSelector(
    [selectMainMenuOptions, selectLoanType, selectSelectedLoanNumber],
    (menuOptions: MainMenuOptions[], optionalType: string | null, account: Identifier) => {
        const { servicingLoanType, inProgressLoanType, servicingType } = CodingConstants;
        const options: MainMenuOptions[] = [];
        const selectedLoanType = get(account, ['loanType'], 'default');
        const type = !optionalType ? get(account, ['type'], optionalType) : optionalType;
        menuOptions?.forEach((ele: MainMenuOptions) => {
            if (ele.subMenuOptions) {
                const subMenuOptions: NavigationOptions[] = [];
                let checkIfSelectedLoanTypeMatchesSubMenuOptions = false;
                ele.subMenuOptions.map(opt => {
                    if (
                        opt.loanType === 'common' ||
                        (opt.loanType === 'default' && selectedLoanType === 'Other') ||
                        opt.loanType === selectedLoanType
                    ) {
                        subMenuOptions.push(opt);
                        checkIfSelectedLoanTypeMatchesSubMenuOptions =
                            opt.loanType === selectedLoanType ? true : checkIfSelectedLoanTypeMatchesSubMenuOptions;
                    }
                });
                if (!checkIfSelectedLoanTypeMatchesSubMenuOptions && selectedLoanType !== 'Other') {
                    const index = ele.subMenuOptions.findIndex(e => e.loanType === 'default');
                    if (index !== -1) {
                        subMenuOptions.push(ele.subMenuOptions[index]);
                    }
                }

                if (type === servicingLoanType || type === inProgressLoanType) {
                    options.push({ ...ele, subMenuOptions });
                } else {
                    if (ele.menuType !== servicingType) {
                        const subOptionType = subMenuOptions.filter(ele => ele.menuType !== servicingType);
                        options.push({ ...ele, subMenuOptions: subOptionType });
                    }
                }
            } else {
                options.push({ ...ele });
            }
        });
        return options;
    }
);

export const selectNLIPWidgetsList = createSelector([selectLoanNumberSelected, selectWidgetsData], (loanNumber: string, cards: any) => {
    if (isEmpty(cards) || isEmpty(loanNumber)) return [];
    return cards
        ?.filter((card: { initParam: any }) => card.initParam)
        .map((elem: { initParam: any[]; id: string }) => elem?.initParam?.find(el => el.value == loanNumber) && elem.id);
});

export const selectLoanStatusByLoanNumberV2 = createSelector([selectWidgetsData, selectLoanNumberSelected], (cards, loanNumber) => {
    if (isEmpty(cards) || isEmpty(loanNumber)) return undefined;
    const data: any = cards.find(ele => ele.id === CodingConstants.loanInProgressWidgetId);
    if (!data) return undefined;
    return data.initParam.find((ele: any) => ele.value === loanNumber)?.loanStatus;
});

export const selectWidgetId = (_: any, id: string) => id;
export const selectEligibilityById = createSelector([selectWidgetsData, selectWidgetId], (widgets: WidgetTemplate[], id: string) => {
    if (isEmpty(widgets) || isEmpty(id)) return false;
    return widgets.some((widget: WidgetTemplate) => widget.id === id);
});

export const selectIsHomeValueEligible = createSelector([selectWidgetsData], (widgets: WidgetTemplate[]) => {
    if (isEmpty(widgets)) return false;
    return widgets.some((widget: WidgetTemplate) => widget.id === CodingConstants.homeValueWidgetId);
});

export const selectCardId = (_: RootState, id: string) => id;
export const selectGroupedByWidgets = createSelector([selectWidgetsData], (widgets: WidgetTemplate[]) => {
    return groupBy(widgets, 'id');
});

export const selectCardById = createSelector(
    [selectGroupedByWidgets, selectCardId],
    (grouped: Record<string, WidgetTemplate[]>, id: string) => {
        return isEmpty(grouped[id]) ? undefined : grouped[id];
    }
);

export const selectCardByIdAndLoanNumber = createSelector(
    [selectGroupedByWidgets, selectCardId, selectLoanNumberSelected],
    (grouped: Record<string, WidgetTemplate[]>, id: string, loanNumber: string) => {
        const hvCards = isEmpty(grouped[id]) ? [] : grouped[id];
        const hvCardForSelectedLoan = hvCards[0]?.initParam?.filter((card: { loanNumber: string }) => card.loanNumber === loanNumber);
        const hvOfferForSelectedLoan = [{ initParam: hvCardForSelectedLoan }];
        return hvOfferForSelectedLoan;
    }
);

export const selectNBPTemplates = createSelector(
    [selectCardById, selectSelectedLoanNumber],
    (cards: WidgetTemplate[] | undefined, account: Identifier) => {
        const initialState = { popups: [] as any, loanNumber: '', portCode: '' };
        if (isEmpty(cards) || isEmpty(account) || isEmpty(account.identifier)) return initialState;
        const [card] = cards;
        const { initParam } = card;

        const groupByLoan: Record<string, WidgetTemplate> = initParam.reduce((r: Record<string, WidgetTemplate>, c: WidgetInitDetails) => {
            return Object.assign(r, { [c.loanNumber]: c });
        }, {} as Record<string, WidgetTemplate>);

        return isEmpty(groupByLoan[account.identifier]) ? initialState : (groupByLoan[account.identifier] as any);
    }
);

export const selectRefactorNBPCard = createSelector(
    [selectNBP, selectSelectedLoanNumber, selectIsHomeValueEligible, selectPropertyCheckValueError],
    (nbpCard, identifier, isHomeValueEligible, propertyValueError): any[] | undefined => {
        if (isEmpty(nbpCard) || isEmpty(identifier)) return undefined;
        const cardByLoanNumber: any[] = get(nbpCard, identifier.identifier, []);
        if (!isEmpty(cardByLoanNumber)) {
            return cardByLoanNumber.filter(
                (el: any) =>
                    el.status === 'pending' &&
                    (el.product === CodingConstants.homeValueWidgetId ? isHomeValueEligible && !propertyValueError : true)
            );
        }
        return undefined;
    }
);

export const selectEligibilityId = createSelector([selectWidgetsData, selectWidgetId], (widgets: WidgetTemplate[], id: string) => {
    if (isEmpty(widgets) || isEmpty(id)) return false;
    return widgets.find((widget: WidgetTemplate) => widget.id === id);
});

export const selectGroupByIdentifiers = createSelector(widgetsSlice, (slice: WidgetState) => {
    return groupBy(slice?.widgets?.profile?.identifiers, 'type');
});

export const selectDashboardType = createSelector(widgetsSlice, (slice: WidgetState) => {
    return slice?.dashboardType;
});

export const selectMessageCenter = createSelector([selectWidgets], (data: Widgets): MainMenuOptions => {
    return data?.mainMenuOptions?.find((options: MainMenuOptions) => options.id === 'Message_Center');
});
